/**
 * Shared helpers for reading/writing an `answers` map at a flat or nested path, plus a
 * recursive client-only-field stripper. Used by both `usePublicForm` (standalone /
 * /agent dynamic form) and `useFormFlow` (multi-step form flow) so upload behaviour
 * is consistent across entry points.
 */

export const NESTED_FIELD_KEY = /^([^[]+)\[(\d+)\]\.(.+)$/;

/**
 * Parse a flat or nested fieldKey and apply `transformer(currentList)` at that path
 * inside `answers`. Returns a new `answers` object — does not mutate input.
 * `transformer` receives the current list (or an empty array) and must return the
 * new list.
 */
export function setAnswerAtPath(answers, fieldKey, transformer) {
  const m = NESTED_FIELD_KEY.exec(fieldKey);
  if (m) {
    const [, section, rowIdxStr, child] = m;
    const rowIdx = Number(rowIdxStr);
    const rows = Array.isArray(answers[section]) ? [...answers[section]] : [];
    while (rows.length <= rowIdx) rows.push({});
    const row = { ...(rows[rowIdx] || {}) };
    const currentList = Array.isArray(row[child]) ? row[child] : [];
    row[child] = transformer(currentList);
    rows[rowIdx] = row;
    return { ...answers, [section]: rows };
  }
  const currentList = Array.isArray(answers[fieldKey]) ? answers[fieldKey] : [];
  return { ...answers, [fieldKey]: transformer(currentList) };
}

export function toArray(value) {
  if (Array.isArray(value)) return value;
  if (value == null) return [];
  return [value];
}

function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj || {}, key);
}

function isNonEmptyAnswer(value) {
  if (value == null) return false;
  if (typeof value === 'string') return value !== '';
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return true;
}

export function valueForField(field, answers = {}) {
  const fieldType = String(field?.type || '');
  if (fieldType === 'display') return '';
  if (fieldType === 'checkbox' || fieldType === 'list') {
    if (hasOwn(answers, field.key)) return toArray(answers?.[field.key]);
    return Array.isArray(field?.defaultValue) ? field.defaultValue : [];
  }
  if (fieldType === 'repeatable_section') {
    const rows = answers?.[field.key];
    if (Array.isArray(rows) && rows.length > 0) return rows;
    return [{}];
  }
  if (fieldType.endsWith('_upload')) return toArray(answers?.[field.key]);
  const raw = answers?.[field.key];
  if ((raw === undefined || raw === null) && field?.defaultValue != null) {
    return field.defaultValue;
  }
  return raw ?? '';
}

export function buildVisibleAnswerPayload(fields, answers = {}) {
  const next = { ...(answers || {}) };
  if (!Array.isArray(fields)) return next;

  for (const field of fields) {
    const key = field?.key;
    if (!key || hasOwn(next, key)) continue;

    const fieldType = String(field?.type || '');
    if (fieldType === 'display' || fieldType === 'repeatable_section' || fieldType.endsWith('_upload')) {
      continue;
    }

    const visibleValue = valueForField(field, answers);
    if (isNonEmptyAnswer(visibleValue)) {
      next[key] = visibleValue;
    }
  }

  return next;
}

const OMIT_VALUE = Symbol('omit-value');

function isFileLikePendingDescriptor(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const id = typeof value.id === 'string' ? value.id : '';
  const hasPendingFlag = value.pending === true || value.uploading === true;
  const hasPendingId = id.startsWith('pending-');
  const hasFileShape =
    typeof value.name === 'string' ||
    typeof value.fileName === 'string' ||
    value.mimeType != null ||
    value.size != null;
  const hasServerDescriptor = value.fileId != null || value.downloadUrl != null;

  return (
    (hasPendingId && (hasPendingFlag || hasFileShape)) ||
    (hasPendingFlag && hasFileShape && !hasServerDescriptor)
  );
}

export function hasPendingUploads(value) {
  if (isFileLikePendingDescriptor(value)) return true;
  if (Array.isArray(value)) return value.some(hasPendingUploads);
  if (value && typeof value === 'object') {
    return Object.values(value).some(hasPendingUploads);
  }
  return false;
}

/**
 * Recursively strip client-only fields (leading underscore, e.g. `_file`) from any
 * descriptor objects inside `value`. Called before saveDraft / submit / localStorage
 * writes so the raw File blob held for Retry never gets serialized.
 */
export function stripClientOnlyFields(value) {
  if (isFileLikePendingDescriptor(value)) {
    return OMIT_VALUE;
  }
  if (Array.isArray(value)) {
    return value
      .map(stripClientOnlyFields)
      .filter((item) => item !== OMIT_VALUE);
  }
  if (value && typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      if (k.startsWith('_')) continue;
      const cleaned = stripClientOnlyFields(v);
      if (cleaned === OMIT_VALUE) continue;
      out[k] = cleaned;
    }
    return out;
  }
  return value;
}
